import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/src/layouts/docs.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Link = makeShortcode("Link");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`Looking for a `}<Link to='/docs/v2/javascript-SDK/quick-start' mdxType="Link">{`quickstart`}</Link>{`?`}</p>
    <p>{`The SDK `}<em parentName="p">{`cannot execute trades or send transactions on your behalf`}</em>{`. Rather, it offers utility classes and functions which make it easy to calculate the data required to safely interact with Uniswap. Nearly everything you need to safely transact with Uniswap is provided by the `}<Link to='/docs/v2/SDK/trade' mdxType="Link">{`Trade`}</Link>{` entity. However, it is your responsibility to use this data to send transactions in whatever context makes sense for your application.`}</p>
    <p>{`This guide will focus exclusively on sending a transaction to the `}<Link to='/docs/v2/smart-contracts/router02' mdxType="Link">{`currently recommended Uniswap router`}</Link>{`.`}</p>
    <h1 {...{
      "id": "sending-a-transaction-to-the-router",
      "style": {
        "position": "relative"
      }
    }}><a parentName="h1" {...{
        "href": "#sending-a-transaction-to-the-router",
        "aria-label": "sending a transaction to the router permalink",
        "className": "anchor before"
      }}><svg parentName="a" {...{
          "aria-hidden": "true",
          "focusable": "false",
          "height": "16",
          "version": "1.1",
          "viewBox": "0 0 16 16",
          "width": "16"
        }}><path parentName="svg" {...{
            "fillRule": "evenodd",
            "d": "M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z"
          }}></path></svg></a>{`Sending a Transaction to the Router`}</h1>
    <p>{`Let’s say we want to trade 1 WETH for as much DAI as possible:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { ChainId, Token, WETH, Fetcher, Trade, Route, TokenAmount, TradeType } from '@uniswap/sdk'

const DAI = new Token(ChainId.MAINNET, '0x6B175474E89094C44Da98b954EedeAC495271d0F', 18)

// note that you may want/need to handle this async code differently,
// for example if top-level await is not an option
const pair = await Fetcher.fetchPairData(DAI, WETH[DAI.chainId])

const route = new Route([pair], WETH[DAI.chainId])

const amountIn = '1000000000000000000' // 1 WETH

const trade = new Trade(route, new TokenAmount(WETH[DAI.chainId], amountIn), TradeType.EXACT_INPUT)
`}</code></pre>
    <p>{`So, we’ve constructed a trade entity, but how do we use it to actually send a transaction? There are still a few pieces we need to put in place.`}</p>
    <p>{`Before going on, we should explore how ETH works in the context of trading. Internally, the SDK uses WETH, as all Uniswap V2 pairs use WETH under the hood. However, it’s perfectly possible for you as an end user to use ETH, and rely on the router to handle converting to/from WETH. So, let’s use ETH.`}</p>
    <p>{`The first step is selecting the appropriate router function. The names of router functions are intended to be self-explanatory; in this case we want `}<Link to='/docs/v2/smart-contracts/router02/#swapexactethfortokens' mdxType="Link">{`swapExactETHForTokens`}</Link>{`, because we’re…swapping an exact amount of ETH for tokens.`}</p>
    <p>{`That Solidity interface for this function is:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-solidity"
      }}>{`function swapExactETHForTokens(uint amountOutMin, address[] calldata path, address to, uint deadline)
  external
  payable
  returns (uint[] memory amounts);
`}</code></pre>
    <p>{`Jumping back to our trading code, we can construct all the necessary parameters:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { Percent } from '@uniswap/sdk'

const slippageTolerance = new Percent('50', '10000') // 50 bips, or 0.50%

const amountOutMin = trade.minimumAmountOut(slippageTolerance).raw // needs to be converted to e.g. hex
const path = [WETH[DAI.chainId].address, DAI.address]
const to = '' // should be a checksummed recipient address
const deadline = Math.floor(Date.now() / 1000) + 60 * 20 // 20 minutes from the current Unix time
const value = trade.inputAmount.raw // // needs to be converted to e.g. hex
`}</code></pre>
    <p>{`The slippage tolerance encodes `}<em parentName="p">{`how large of a price movement we’re willing to tolerate before our trade will fail to execute`}</em>{`. Since Ethereum transactions are broadcast and confirmed in an adversarial environment, this tolerance is the best we can do to protect ourselves against price movements. We use this slippage tolerance to calculate the `}<em parentName="p">{`minumum`}</em>{` amount of DAI we must receive before our trade reverts, thanks to `}<Link to='/docs/v2/SDK/trade/#minimumamountout-since-204' mdxType="Link">{`minimumAmountOut`}</Link>{`. Note that this code calculates this worst-case outcome `}<em parentName="p">{`assuming that the current price, i.e the route’s mid price,`}</em>{` is fair (usually a good assumption because of arbitrage).`}</p>
    <p>{`The path is simply the ordered list of token addresses we’re trading through, in our case WETH and DAI (note that we use the WETH address, even though we’re using ETH). `}</p>
    <p>{`The to address is the address that will receive the DAI.`}</p>
    <p>{`The deadline is the Unix timestamp after which the transaction will fail, to protect us in the case that our transaction takes a long time to confirm and we wish to rescind our trade.`}</p>
    <p>{`The value is the amount of ETH that must be included as the `}<inlineCode parentName="p">{`msg.value`}</inlineCode>{` in our transaction.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      